import { Participant } from "@/types/participant/participant-types";
import useFetch from "../useFetch/useFetch";
import { getCampaign } from "@/services/campaign/campaign";
import { getMissionCustomWithFormatObjective } from "@/services/mission/missions";
import { Campaign } from "@/types/campaign/campaign-type";
import { Mission } from "@/types/mission/mission-type";
import { createParticipantByCampaignId, getParticipantByCampaignId } from "@/services/participant/participant";
import { useCallback, useEffect, useRef, useState } from "react";
import { Objective } from "@/types/objective/objective-type";
import { amplitudeAddTrack } from "@/utils/amplitud/event-track";
import { useUserContext } from "@/contexts/user/useUserContext";

type MissionCustom = {
	allMissions: Mission[];
	allRequirements: Objective[];
}

const useCampaignMission = (campaignId: number, missionId: number) => {
	const { isConnect } = useUserContext();
	const params = {
		limit: 1,
		page: 0,
		cache: true
	}
	const {
		data: campaign,
		loading: loadingCampaign,
	} = useFetch<Campaign[], object>(getCampaign, { id: campaignId, ...params }, { autoFetch: true });
	const {
		data: dataMissionCustom,
		loading: loadingMissions,
	} = useFetch<MissionCustom, object>(getMissionCustomWithFormatObjective, { campaign_id: campaignId, id: missionId, ...params }, { autoFetch: true });
	const { allMissions, allRequirements } = dataMissionCustom;
	const {
		data: participant,
		loading: loadingParticipant,
		startFetch: startFetchParticipant
	} = useFetch<Participant[], object>(getParticipantByCampaignId, { campaign_id: campaignId }, { autoFetch: false });

	const [loadingCreateParticipant, setLoadingCreateParticipant] = useState(loadingParticipant ?? false);
	const loadingCampaignOrParticipant = loadingCreateParticipant || loadingCampaign || loadingParticipant;

	const handleCreateParticipant = useCallback(async () => {
		try {
			amplitudeAddTrack('Create Participant', {
				campaign_id: campaignId,
				timestamp: new Date().toISOString(),
			});
			setLoadingCreateParticipant(true);
			await createParticipantByCampaignId({ campaign_id: campaignId });
			await startFetchParticipant();
		} catch (error) {
			console.error(error);
		} finally {
			setLoadingCreateParticipant(false);
		}
	}, [campaignId, startFetchParticipant]);


	const firstRender1 = useRef(true);
	const firstRender2 = useRef(true);
	useEffect(() => {
		if (!loadingParticipant && 
			isConnect &&
			firstRender1.current &&
			!firstRender2.current &&
			participant?.length === 0 || (participant?.[0] && !participant[0].user_main_key)
		) {
			firstRender1.current = false;
			handleCreateParticipant();
		}
	}, [isConnect, loadingParticipant, participant, handleCreateParticipant]);

	useEffect(() => {
		if (isConnect && firstRender2.current) {
			firstRender2.current = false;
			startFetchParticipant();
		}
	}, [isConnect, startFetchParticipant]);


	return {
		campaign: campaign[0],
		requirements: allRequirements,
		missions: allMissions,
		participant,
		loadingCampaignOrParticipant,
		loadingMissions,
		error: null
	};
};

export default useCampaignMission;